import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useRouteIds } from '../../../../hooks/useRouteIds';
import { GroupSuccessScreen } from './group-success-screen';
import { GroupLoadingScreen } from './group-loading-screen';
import { GroupFormScreen } from './group-form-screen';
import { useCoaGroupForm } from './use-coa-group-form';
import { COADisplayAccount } from '../../../../chart-of-accounts/types';
import { buildGroupStructurePayload } from '../../../../chart-of-accounts/utils';
import { useCoaApiView } from '../../../../chart-of-accounts/hooks/useCoaApiView';

type Screen = 'form' | 'saving' | 'saved';

export interface CoaGroupWizardContainerProps {
  onClose: () => void;
  row?: COADisplayAccount;
}

export const CoaGroupWizardContainer = ({ onClose, row }: CoaGroupWizardContainerProps) => {
  const { workspaceId, organisationId } = useRouteIds();

  const {
    profitAndLossTree,
    balanceSheetTree,

    // Create Flow
    createGroupAndUpdateStructure,
    isCreatingGroupAndStructure,
    errorCreatingGroupAndStructure,

    // Edit Flow
    updateGroup,
    isUpdatingGroup,
    errorUpdatingGroup,

    // Structure Update Flow (Used by Edit)
    isUpdatingStructure,
    errorUpdatingStructure,
    updateStructure
  } = useCoaApiView(workspaceId, organisationId);

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const intl = useIntl();

  const {
    groupName,
    setGroupName,
    errors,
    validate,
    revalidate,
    category,
    setSelectedCategory,
    categoryOptions,
    accountOptions,
    selectedAccounts,
    setSelectedAccounts,
    isBalanceSheet,
    isEditMode
  } = useCoaGroupForm(profitAndLossTree, balanceSheetTree, row);

  const isSaving = isCreatingGroupAndStructure || isUpdatingGroup || isUpdatingStructure;
  const hasError = errorCreatingGroupAndStructure || errorUpdatingGroup || errorUpdatingStructure;

  useEffect(() => {
    if (hasError) {
      setError(
        intl.formatMessage({
          id: 'reporting.try_again',
          defaultMessage: 'Try again.'
        })
      );
      setScreen('form');
    }
  }, [intl, hasError]);

  useEffect(() => {
    if (isSaving) {
      setScreen('saving');
    }
  }, [isSaving]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      return;
    }
    setError(undefined);

    if (!profitAndLossTree || !balanceSheetTree) {
      return;
    }

    if (isEditMode && row) {
      updateGroup(
        { groupId: row.id, data: { name: groupName } },
        {
          onSuccess: () => {
            const payload = buildGroupStructurePayload(
              row.id,
              groupName,
              category,
              isBalanceSheet,
              profitAndLossTree,
              balanceSheetTree,
              selectedAccounts,
              true
            );

            updateStructure(payload, {
              onSuccess: () => setScreen('saved'),
              onError: () => setScreen('form')
            });
          },
          onError: () => setScreen('form')
        }
      );
    } else {
      createGroupAndUpdateStructure(
        {
          groupData: { name: groupName, category },
          buildStructurePayload: newGroup => {
            return buildGroupStructurePayload(
              newGroup.id,
              groupName,
              category,
              isBalanceSheet,
              profitAndLossTree,
              balanceSheetTree,
              selectedAccounts,
              false
            );
          }
        },
        {
          onSuccess: () => setScreen('saved'),
          onError: () => setScreen('form')
        }
      );
    }
  }, [
    isEditMode,
    row,
    category,
    groupName,
    isBalanceSheet,
    selectedAccounts,
    validate,
    profitAndLossTree,
    balanceSheetTree,
    updateGroup,
    updateStructure,
    createGroupAndUpdateStructure
  ]);

  return (
    <>
      {screen === 'form' && (
        <GroupFormScreen
          error={error}
          validationErrors={errors}
          groupName={groupName}
          onGroupNameChange={setGroupName}
          category={category}
          setSelectedCategory={setSelectedCategory}
          categories={categoryOptions}
          onClose={onClose}
          accountOptions={accountOptions}
          onCreate={handleSubmit}
          revalidate={revalidate}
          onBlur={revalidate}
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          isEditMode={isEditMode}
        />
      )}
      {screen === 'saving' && <GroupLoadingScreen isEditMode={isEditMode} />}
      {screen === 'saved' && <GroupSuccessScreen onClose={onClose} isEditMode={isEditMode} />}
    </>
  );
};
